import { useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router';
import { Text, theme, styles as baseStyles } from '@socrait/react';
import { QueryStatus } from '@tanstack/react-query';

import { ErrorBox } from '~/components/ErrorBox';
import { Loading } from '~/components/Loading';
import { useViewClassroomMomentsFeature, useViewWeeklyInsightsFeature } from '~/featureFlags';
import { InsightsResponse, MomentsResponse, ReviewActionItemsResponse } from '~/services/review';
import { ActionItem, Period } from '~/services/types';
import { getStudentDataPath } from '~/screens/studentData';
import { getMeetingContentState } from '~/pages/review/utils/meetingContentState';

import { ActionAccordionItem } from './ui/actionItem/ActionAccordionItem';
import { DataLogActionItem } from './ui/actionItem/DataLogActionItem';
import { ClassroomMoments } from './ui/ClassroomMoments';
import { DeleteReviewModal } from './ui/DeleteReviewModal';
import { Insights } from './ui/Insights';
import { ReviewKebabMenu } from './ui/ReviewKebabMenu';

interface ReviewActionItemsProps {
  period: Period;
  date: Date;
  hasPraiseOrWarningItems: boolean;
  actionItemsData?: ReviewActionItemsResponse | null;
  activeMeetingId?: string;
  actionItemsQueryStatus: QueryStatus;
  actionItemsError?: Error | null;
  insightsData?: InsightsResponse | null;
  insightsQueryStatus: QueryStatus;
  momentsData?: MomentsResponse | null;
  momentsQueryStatus: QueryStatus;
}

const isNew = ({ status }: ActionItem) => status === 'new';
const isClassSummary = ({ type }: ActionItem) => type === 'class_summary';

const filterActionItems = (items: ActionItem[]): ActionItem[] => {
  let summaryItem: ActionItem | undefined;
  const otherItems: ActionItem[] = [];

  for (const item of items) {
    if (!isNew(item)) {
      continue;
    }

    if (isClassSummary(item)) {
      if (!summaryItem) {
        summaryItem = item;
      }
      continue;
    }

    otherItems.push(item);
  }

  return [summaryItem, ...otherItems].filter(Boolean) as ActionItem[];
};

export const ReviewActionItems = ({
  period,
  date,
  hasPraiseOrWarningItems,
  actionItemsData,
  activeMeetingId,
  actionItemsQueryStatus,
  actionItemsError,
  insightsData,
  insightsQueryStatus,
  momentsData,
  momentsQueryStatus,
}: ReviewActionItemsProps) => {
  const [isDeleteReviewModalOpen, setIsDeleteReviewModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const viewWeeklyInsightsFeature = useViewWeeklyInsightsFeature();
  const viewClassroomMomentsFeature = useViewClassroomMomentsFeature();
  const navigate = useNavigate();

  const closeMenu = () => setIsMenuOpen(false);

  const contentState = getMeetingContentState(actionItemsData?.meetings, activeMeetingId, actionItemsData?.status);
  const canDelete = (actionItemsData?.meetings?.length ?? 0) > 0;

  const reviewActionItemsContent = useMemo(() => {
    if (contentState.kind === 'no-meetings') {
      const isProcessedEmpty = contentState.dayStatus === 'processed';
      return (
        <View style={styles.noDataContainer}>
          <Text style={styles.noDataTitle}>
            {isProcessedEmpty ? 'No class content found for this day.' : "There's nothing here yet!"}
          </Text>
          {!isProcessedEmpty && <Text style={styles.noDataText}>Waiting for data from your class</Text>}
        </View>
      );
    }

    if (contentState.kind === 'processing') {
      return (
        <View style={styles.noDataContainer}>
          <Text style={styles.noDataTitle}>Processing…</Text>
        </View>
      );
    }

    if (contentState.kind === 'failed') {
      return (
        <ErrorBox
          text={`Something went wrong processing this class. Contact support with meeting id: ${contentState.meetingId}`}
        />
      );
    }

    const actionItems = contentState.meeting.actionItems;
    if (actionItems.length === 0 && !hasPraiseOrWarningItems) {
      return (
        <View style={styles.noDataContainer}>
          <Text style={styles.noDataTitle}>There's nothing here yet!</Text>
          <Text style={styles.noDataText}>Waiting for data from your class</Text>
        </View>
      );
    }

    const meetingId = contentState.meeting.meetingId;
    const processedActionItems = filterActionItems(actionItems);

    return (
      <>
        {processedActionItems.map((item) => {
          const onGoToCustomMessages = item.studentVoiceName
            ? () => navigate(getStudentDataPath(period.shortName, item.studentVoiceName!, 'customMessages'))
            : undefined;
          return (
            <ActionAccordionItem
              key={item.id}
              actionId={item.id}
              title={item.title}
              type={item.type}
              studentId={item.studentId}
              studentVoiceName={item.studentVoiceName}
              message={item.message}
              date={date}
              periodId={period.id}
              meetingId={meetingId}
              onGoToCustomMessages={onGoToCustomMessages}
            />
          );
        })}
        {hasPraiseOrWarningItems && (
          <DataLogActionItem date={date} title={'Data Log'} periodId={period.id} meetingId={meetingId} />
        )}
      </>
    );
  }, [contentState, hasPraiseOrWarningItems, date, period.id, period.shortName, navigate]);

  if (actionItemsQueryStatus === 'pending') {
    return <Loading backgroundColor={theme.colors.transparent} />;
  }

  if (actionItemsError) {
    return <ErrorBox text="Could not load action items. Please try refreshing the page." />;
  }

  return (
    <View style={styles.container}>
      {viewWeeklyInsightsFeature && <Insights data={insightsData} status={insightsQueryStatus} />}
      {viewClassroomMomentsFeature && (
        <ClassroomMoments data={momentsData} status={momentsQueryStatus} activeMeetingId={activeMeetingId} />
      )}
      <View style={styles.actionAccordionItems}>
        <View style={styles.actionTitleContainer}>
          <Text style={styles.actionTitle}>Suggested Actions</Text>
          {canDelete && (
            <ReviewKebabMenu
              isMenuOpen={isMenuOpen}
              toggleMenu={() => setIsMenuOpen((prev) => !prev)}
              closeMenu={closeMenu}
              setIsDeleteReviewModalOpen={setIsDeleteReviewModalOpen}
            />
          )}
        </View>
        {reviewActionItemsContent}
        <DeleteReviewModal
          period={period}
          date={date}
          isOpen={isDeleteReviewModalOpen}
          onClose={() => setIsDeleteReviewModalOpen(false)}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },
  actionTitleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: 40,
  },
  actionTitle: {
    ...baseStyles.title,
    paddingHorizontal: theme.spacing.medium,
    paddingVertical: theme.spacing.xsmall,
    fontSize: theme.fontSizes.small,
  },
  actionAccordionItems: {
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },
  noDataContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xlarge,
  },
  noDataTitle: {
    ...baseStyles.title,
    color: theme.colors.darkGray,
    lineHeight: theme.lineHeights.regular * 2,
    textAlign: 'center',
  },
  noDataText: {
    color: theme.colors.darkGray,
    lineHeight: theme.lineHeights.regular,
    textAlign: 'center',
  },
});
